import React, { useEffect, useId, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import type { MenuItem } from '@/types/menu'
import { useLocale, pickI18n } from '@/hooks/useLocale'
import { Collapse } from './Collapse'

interface Props {
  items: MenuItem[]
  activeItemId: string | null
  searching: boolean
  renderLeaf: (item: MenuItem) => React.ReactNode
}

const containsItem = (item: MenuItem, id: string | null): boolean =>
  String(item.id) === id || (item.children ?? []).some((child) => containsItem(child, id))

function Directory({ item, ...props }: Omit<Props, 'items'> & { item: MenuItem }) {
  const locale = useLocale()
  const contentId = useId()
  const active = containsItem(item, props.activeItemId)
  const [expanded, setExpanded] = useState(active)
  useEffect(() => {
    if (active) setExpanded(true)
  }, [active, props.activeItemId])
  const open = props.searching || expanded
  return <div className="nv-directory">
    <button type="button" className="nv-subhead" aria-expanded={open} aria-controls={contentId}
      disabled={item.isActive === false}
      onClick={() => { if (!props.searching) setExpanded((value) => !value) }}>
      <ChevronRight size={16} strokeWidth={1.7} style={{ transform: open ? 'rotate(90deg)' : undefined }} />
      <span>{pickI18n(item.labelI18n, locale, item.name)}</span>
    </button>
    <Collapse open={open}>
      <div id={contentId} className="nv-branches"><MenuBranches {...props} items={item.children ?? []} /></div>
    </Collapse>
  </div>
}

export function MenuBranches(props: Props) {
  return <>{props.items.map((item) => item.type === 1
    ? <Directory key={item.id} item={item} activeItemId={props.activeItemId} searching={props.searching} renderLeaf={props.renderLeaf} />
    : <React.Fragment key={item.id}>{props.renderLeaf(item)}</React.Fragment>)}</>
}
